import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import HTMLParser from 'html-react-parser';
import { useApp } from '@/lib/core/providers/appProvider';
import { useClientTranslation } from '@/lib/core/i18n';
import type { TranslationKey } from '@/lib/core/i18n/types';
import { useLocaleStore, useUIStore } from '@/lib/core/stores';
import { initializeTalkdeskChat } from '@/lib/core/utils';
import { cn } from '@/lib/core/utils/cn';
import { ScreenHead } from '@/components/shared/screen/head';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { NotFoundError } from '@/components/shared/errors';
import { BookingDetailInfo, type InfoItem } from '@/components/shared/booking/common/detail/info';
import { AccordionBlock } from '@/components/shared/explore/detail/accordion-block';
import { STRUCTURED_CONTENT_KEYS, useEventContentLabel } from './contentKeys';
import { AssistanceBlock } from '@/components/shared/explore/ui/assistance-block';
import {
  CoveringFooter,
  coveredPanelClassName,
  useCoveredBottomPanel,
} from '@/components/shared/explore/ui/covering-footer';
import { shareContent, toShareUrl } from '@/lib/core/utils/share';
import { addToCalendar } from '@/lib/core/utils/calendar';
import { formatAmount } from '@/lib/core/utils/currency';
import { useEventDetail } from '@/services/platform/data/events';
import { formatCardDateTime } from '../list/format';
import { isMembersOnly } from '../list/visibility';
import { useEventCategoryLabel } from '../list/categories';
import {
  allowsEventBooking,
  campaignServerDays,
  isEveryOccurrenceClosed,
  isEveryOccurrenceSoldOut,
  resolveBookingScope,
  resolveGuestLimit,
  resolvePaymentDecision,
} from '../rules';
import { InterestToggle } from './InterestToggle';
import { useEventCategoryInterest } from './useEventCategoryInterest';
import { InfoWarningCard, type InfoWarningLine } from './InfoWarningCard';
import { SoldOutBadge } from './SoldOutBadge';

/**
 * `media[]` keys agreed with the events team (contract v1.12 §5) — the team names each
 * uploaded file with its key and we render by key. `image`/`menu` are deprecated.
 */
const MEDIA_KEY_MENU = 'menu_pdf';
const MEDIA_KEY_HERO = 'hero_image';

const RECURRENCE_LABEL_KEYS: Record<string, TranslationKey> = {
  DAILY: 'App.Events.Recurrence.Daily',
  WEEKLY: 'App.Events.Recurrence.Weekly',
  MONTHLY: 'App.Events.Recurrence.Monthly',
};

/** Fallback hero date line for a recurring event with no single fixed `startDateTime` (a continuous campaign). */
function recurrenceLabel(t: (key: TranslationKey) => string, frequency?: string | null): string | undefined {
  if (!frequency) return undefined;
  const key = RECURRENCE_LABEL_KEYS[frequency.toUpperCase()];
  return (key ? t(key) : frequency).toUpperCase();
}

/**
 * Event Detail Page (EDP) — one component branching on the event's data:
 * bookable (`allowsEventBooking` → "Book now" into the event book flow) vs
 * courtesy/no-booking (→ add-to-calendar). Independently, `resolvePaymentDecision`
 * (contract v1.12 §3.1.2) drives the price line and blocks the CTA when an event
 * is marked paid but has no price yet ("notForSale"). Fetched by GUID, no CMS
 * content backing — same pattern as `VenueDetailScreen`.
 *
 * Not yet covered: a paid event's checkout step (no Stripe integration exists —
 * `payment === 'paid'` degrades to the concierge-arranged registration), and the
 * already-registered state (`myRegistration` is unread).
 */
export function EventDetailScreen() {
  // `*` is the whole remainder after the locale segment (e.g.
  // `events-calendar/event-detail/{id}` — the CMS `eventDetail` route path is
  // nested, not a single segment) — the id is always its last part.
  const { '*': eventPath = '' } = useParams();
  const eventId = eventPath.split('/').filter(Boolean).pop() ?? '';
  const navigate = useNavigate();
  const location = useLocation();
  // Forwarded from the Events Calendar screen when the user tapped a card under a specific
  // day heading — relayed on to the booking flow's "Book now" so it can preselect the day.
  const selectedDate = (location.state as { selectedDate?: string } | null)?.selectedDate;
  const t = useClientTranslation();
  const getCategoryLabel = useEventCategoryLabel();
  const getContentLabel = useEventContentLabel();
  const locale = useLocaleStore(state => state.locale);
  const { user } = useApp();
  const setMainClassName = useUIStore(state => state.setMainClassName);

  const isMember = !!user?.hasActiveMembership;

  const { event, isLoading } = useEventDetail(eventId);

  useEffect(() => {
    setMainClassName('pb-0');
    return () => setMainClassName('');
  }, [setMainClassName]);

  const { panelRef, footerRef, panelHeight, isPanelCovered } = useCoveredBottomPanel();

  const { isSelected: isCategoryInterest, toggle: toggleCategoryInterest } = useEventCategoryInterest(
    event?.eventCategory
  );

  if (isLoading) return <SpinLoading wrapperClassName="min-h-[50vh]" />;
  if (!event) return <NotFoundError />;

  const membersOnly = isMembersOnly(event.visibility);
  // Scoped to occurrence #1 (mirrors the hero/top-level fields above) — a multi-occurrence event
  // may price its dates differently; showing "from" pricing is a follow-up, not this round's scope.
  const scope = resolveBookingScope(event, event.occurrences?.[0] ?? null);
  const canBook = allowsEventBooking(event);
  const payment = resolvePaymentDecision(scope);
  const guestLimit = resolveGuestLimit(scope);
  // Aggregated across every occurrence — the event-level mirror (occurrence #1 only) must not
  // gate the whole event: a sold-out/expired first date should not disable a still-open one.
  const isSoldOut = isEveryOccurrenceSoldOut(event);
  const registrationClosed = isEveryOccurrenceClosed(event);

  const media = event.media ?? [];
  const menuUrl = media.find(item => item.key === MEDIA_KEY_MENU)?.url ?? undefined;
  // `imageUrl` is a nullable mirror of media[hero_image]; media[] is the contractual source.
  const heroImageUrl = media.find(item => item.key === MEDIA_KEY_HERO)?.url ?? event.imageUrl;

  // The day the hero shows: the one tapped in the calendar, else — for a campaign, whose own start is
  // its long-past anchor — its next run day; any other event keeps its own start/end.
  const days = event.days ?? [];
  const shownDay =
    days.find(day => !!selectedDate && day.date === selectedDate) ??
    (campaignServerDays(event) ? days[0] : undefined);
  const shownStart = shownDay?.startDateTime ?? event.startDateTime;
  const shownEnd = shownDay ? shownDay.endDateTime : event.endDateTime;

  const heroDateTime =
    formatCardDateTime(shownStart, locale, shownEnd)?.toUpperCase() ??
    recurrenceLabel(t, event.recurrence?.frequency);
  const heroRows: InfoItem[][] = [
    heroDateTime ? [{ icon: 'calendar', text: heroDateTime }] : [],
    scope.venueName ? [{ icon: 'map-pin', text: scope.venueName.toUpperCase() }] : [],
  ];

  // Cortesia (nobody pays) and "no prior booking required" are independent axes (contract
  // v1.12 §3.1.2 vs §3.2) — an event can be either, both, or neither. `CourtesyNotice` now
  // triggers on `isComplimentary`, not on the booking mode, so a bookable event missing only
  // its Salesforce `bookingMode` no longer gets mislabelled as a courtesy.
  const infoLines: InfoWarningLine[] = [];
  if (payment === 'complimentary') {
    infoLines.push({ icon: 'coin-slash', text: t('App.Events.CourtesyNotice') });
  }
  infoLines.push(
    canBook
      ? { icon: 'plus-square', text: t('App.Events.PriorBookingRequired') }
      : { icon: 'minus-square', text: t('App.Events.NoBookingRequired') }
  );
  if (payment === 'paid') {
    const price = formatAmount(scope.price, scope.currencyCode, locale ?? 'en-GB');
    infoLines.push({ icon: 'coins', text: t('App.Events.PriceLine', { price }) });
    if (canBook) infoLines.push({ icon: 'info-circle', text: t('App.Events.PaymentHandledByConcierge') });
  } else if (payment === 'notForSale') {
    infoLines.push({ icon: 'info-circle', text: t('App.Events.PriceToBeAnnounced') });
  }
  if (guestLimit) {
    infoLines.push({ icon: 'info-circle', text: t('App.Events.TicketLimit', { count: guestLimit }) });
  }
  if (registrationClosed) {
    infoLines.push({ icon: 'info-circle', text: t('App.Events.RegistrationClosedInfo') });
  }

  const contents = [...(event.contents ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const eventInfo = contents.find(content => content.contentKey === 'event_info')?.contentValue;
  const accordionItems = contents
    .filter(content => !STRUCTURED_CONTENT_KEYS.includes(content.contentKey ?? ''))
    .map(content => ({
      title: getContentLabel(content.contentKey),
      description: content.contentValue,
    }));

  function handleAddToCalendar() {
    // Non-members cannot interact with a members-only courtesy event — no-op.
    if (!isMember && membersOnly) return;
    if (!shownStart) return;
    addToCalendar({
      title: event?.title ?? '',
      startDate: new Date(shownStart),
      endDate: shownEnd ? new Date(shownEnd) : undefined,
    });
  }

  function handleBookNow() {
    if (isSoldOut || registrationClosed || payment === 'notForSale') return;
    if (!isMember && membersOnly) return;

    const bookingMode = event?.bookingMode;
    switch (bookingMode) {
      case 'Concierge':
        initializeTalkdeskChat();
        return;
      case 'External': {
        const ref = event?.occurrences?.[0]?.bookingRef ?? event?.bookingRef;
        if (ref) window.open(ref, '_blank', 'noopener,noreferrer');
        return;
      }
      case 'Salesforce':
      case 'SevenRooms':
      default: {
        const localeSegment = locale?.split('-')[0] ?? 'en';
        navigate(`/${localeSegment}/book/event-book-flow`, { state: { eventId: event?.id, selectedDate } });
      }
    }
  }

  function handleDiscoverTheMenu() {
    if (!menuUrl) return;
    window.open(menuUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="bg-jncquoi-neutral-00 flex min-h-svh flex-col">
      <div className="from-jncquoi-neutral-1000 fixed top-0 right-0 left-0 z-50 bg-linear-to-b from-0% to-transparent to-[81.25%] backdrop-blur-[2.5px]">
        <ScreenHead
          title=""
          variant="overlay"
          onBack={() => navigate(-1)}
          ariaLabel={t('App.Labels.GoBack')}
          className="pt-safe-area"
          rightActions={
            <button
              type="button"
              aria-label={t('App.Labels.Share')}
              className="flex size-11 items-center justify-center"
              onClick={() =>
                shareContent({ title: event.title ?? '', url: toShareUrl(window.location.href) })
              }
            >
              <Icon
                name="share-ios"
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-jncquoi-neutral-00"
              />
            </button>
          }
        />
      </div>

      <div className="relative h-[65svh] w-full shrink-0">
        {heroImageUrl ? (
          <img
            src={heroImageUrl}
            alt={event.title ?? ''}
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <div className="bg-jncquoi-neutral-100 absolute inset-0" aria-hidden="true" />
        )}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.45) 3.64%, rgba(0,0,0,0.045) 21.79%, rgba(0,0,0,0.045) 49.62%, rgba(0,0,0,0.9) 79.62%)',
          }}
        />
        <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col items-center gap-6 px-4 pb-10">
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="font-didot-lt-std text-jncquoi-neutral-00 text-[40px] leading-[40px] font-black tracking-[-1.6px] uppercase">
              {event.title}
            </p>
          </div>
          <BookingDetailInfo rows={heroRows} />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-14 px-4 pt-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              {membersOnly && (
                <>
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
                    {t('App.Labels.MembersOnly')}
                  </span>
                  <span className="text-jncquoi-neutral-500 text-[4px]" aria-hidden="true">
                    ●
                  </span>
                </>
              )}
              {event.eventCategory && (
                <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
                  {getCategoryLabel(event.eventCategory)}
                </span>
              )}
              {event.eventCategory && (
                <InterestToggle
                  size={16}
                  isSelected={isCategoryInterest}
                  onToggle={toggleCategoryInterest}
                  disabled={!isMember}
                />
              )}
            </div>
            <h1 className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[24px] font-semibold">
              {event.title}
            </h1>
            <div className="flex flex-col gap-2">
              {(event.venueAddress || scope.venueName) && (
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] font-semibold tracking-[0.25px]">
                  {event.venueAddress || scope.venueName}
                </p>
              )}
              {isSoldOut && <SoldOutBadge />}
            </div>
          </div>
          <InfoWarningCard lines={infoLines} />
        </div>

        {(event.richDescription || event.description) && (
          <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] tracking-[0.25px]">
            {HTMLParser(event.richDescription || event.description || '')}
          </div>
        )}

        {eventInfo && (
          <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] tracking-[0.25px]">
            {HTMLParser(eventInfo)}
          </div>
        )}

        <AccordionBlock items={accordionItems} />
      </div>

      <CoveringFooter footerRef={footerRef} panelHeight={panelHeight} isPanelCovered={isPanelCovered}>
        <AssistanceBlock />
      </CoveringFooter>

      <div
        ref={panelRef}
        className={cn(
          'border-jncquoi-neutral-200 bg-jncquoi-neutral-00 flex flex-col gap-2 border-t px-4 pt-6 pb-10.5',
          coveredPanelClassName(isPanelCovered)
        )}
      >
        {canBook ? (
          <>
            <Button
              variant="primary-solid"
              palette="dark"
              size="md"
              className="w-full"
              disabled={
                isSoldOut || registrationClosed || payment === 'notForSale' || (!isMember && membersOnly)
              }
              onClick={handleBookNow}
            >
              {isSoldOut
                ? t('App.Events.SoldOut')
                : registrationClosed
                  ? t('App.Events.RegistrationClosed')
                  : payment === 'notForSale'
                    ? t('App.Events.NotAvailableForBooking')
                    : t('App.Events.BookNow')}
            </Button>
            {/* Only offered when the events team actually uploaded a menu for this event. */}
            {menuUrl && (
              <Button
                variant="secondary-outline"
                palette="dark"
                size="md"
                className="w-full"
                onClick={handleDiscoverTheMenu}
              >
                {t('App.Events.DiscoverTheMenu')}
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="primary-solid"
            palette="dark"
            size="md"
            className="w-full"
            onClick={handleAddToCalendar}
          >
            {t('App.Events.AddToCalendar')}
          </Button>
        )}
      </div>
    </div>
  );
}
